import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { keyframes } from "@emotion/react";

const MAX_DURATION_MS = 5 * 60 * 1000;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

function formatRemaining(ms: number): string {
  const remaining = Math.max(0, MAX_DURATION_MS - ms);
  const totalSeconds = Math.ceil(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}

interface RecordingOverlayProps {
  elapsedMs: number;
  hasAudio: boolean;
  onStop: () => void;
}

const RecordingOverlay: React.FC<RecordingOverlayProps> = ({
  elapsedMs,
  hasAudio,
  onStop,
}) => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        bgcolor: "rgba(0, 0, 0, 0.78)",
        color: "#fff",
        borderRadius: "9999px",
        px: 2,
        py: 1,
        zIndex: 1200,
        boxShadow: 3,
      }}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: "#ef4444",
          animation: `${pulse} 1.2s ease-in-out infinite`,
        }}
      />
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontWeight: 600, letterSpacing: 0.5 }}
      >
        {formatRemaining(elapsedMs)}
      </Typography>
      <Tooltip
        title={hasAudio ? "Microphone is on" : "Microphone disabled"}
        placement="bottom"
      >
        {hasAudio ? (
          <MicIcon fontSize="small" sx={{ opacity: 0.9 }} />
        ) : (
          <MicOffIcon fontSize="small" sx={{ opacity: 0.6 }} />
        )}
      </Tooltip>
      <Tooltip title="Stop recording" placement="bottom">
        <IconButton
          size="small"
          onClick={onStop}
          sx={{
            color: "#fff",
            bgcolor: "#ef4444",
            "&:hover": { bgcolor: "#dc2626" },
          }}
        >
          <StopIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default RecordingOverlay;
